import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { MemberSeat } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import {
  BATCH_LANDED,
  isLandedUnreadable,
  type BatchSeatResult,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { buildReport, outcomeProse, studentTally, type ReportRow } from './batchReport';
import {
  holdersWithoutEligibleSeat,
  seatContextBySeatId,
  type SeatContext,
  type SeatHolder,
} from './memberSeats';

const { seatActions } = appStrings.modules.coachingDashboard;
const strings = seatActions.cohortOutcome;

/** How many refused people a one-line notice names before it counts the rest. */
const NAMES_SHOWN = 5;

export type CohortOutcomeSeverity = 'success' | 'warning' | 'error';

export type CohortOutcome = Readonly<{
  landed: number;
  total: number;
  /** Who came back wholly landed, so a selection drops them and keeps the rest. */
  landedHolderIds: readonly number[];
  message: string;
  severity: CohortOutcomeSeverity;
}>;

type CohortOutcomeArgs = Readonly<{
  results: readonly BatchSeatResult[];
  eligible: (seats: readonly MemberSeat[]) => MemberSeat[];
  /** Which word this door calls a landing. Anything else the batch reports is a skip. */
  landedWord: typeof BATCH_LANDED.add | typeof BATCH_LANDED.remove;
  cohortName: string;
}> &
  /** One person's door names them; the multi-select door counts them. Carrying the single
   * holder in the shape keeps the naming voice from being asked for a batch it cannot name. */
  (| Readonly<{ voice: 'holder'; holder: SeatHolder }>
    | Readonly<{ voice: 'selection'; holders: readonly SeatHolder[] }>
  );

/**
 * One line per person rather than per seat. Someone whose two seats were refused for two
 * different reasons is named once, with the first — a sentence that listed both would be
 * longer than the notice it rides in.
 */
const refusedNames = (rows: readonly ReportRow[]): string => {
  const byHolder = new Map<string, ReportRow>();

  rows
    .filter((row) => !row.landed)
    .forEach((row) => {
      if (!byHolder.has(row.holderKey)) byHolder.set(row.holderKey, row);
    });

  const named = Array.from(byHolder.values(), (row) =>
    strings.person(row.name, outcomeProse(row.outcome)),
  );

  return (
    named.length <= NAMES_SHOWN
      ? named
      : [...named.slice(0, NAMES_SHOWN), strings.andMore(named.length - NAMES_SHOWN)]
  ).join('; ');
};

/**
 * Someone landed only if every seat they submitted landed. A holder half of whose seats were
 * refused has not gone through, so the selection keeps them. Read off the seat ids rather than
 * a report row's key, which is minted inside the report and says nothing about whose it is.
 */
const landedHolderIds = (
  results: readonly BatchSeatResult[],
  contextBySeatId: ReadonlyMap<number, SeatContext>,
  landedWord: string,
): number[] => {
  const wholly = new Map<number, boolean>();

  results.forEach((result) => {
    const holder = contextBySeatId.get(result.rosterSeatId)?.holder;

    if (holder === undefined) return;

    wholly.set(holder.id, (wholly.get(holder.id) ?? true) && result.outcome === landedWord);
  });

  return Array.from(wholly.entries())
    .filter(([, landed]) => landed)
    .map(([id]) => id);
};

/**
 * What a cohort membership batch did, read off the outcomes it answered with. Every word
 * other than the door's landing word means that seat did not change, and they all arrive
 * inside a 2xx — so the absence of a throw says nothing about what landed.
 */
export const cohortOutcome = (args: CohortOutcomeArgs): CohortOutcome => {
  const { results, eligible, landedWord, cohortName } = args;
  const holders = args.voice === 'holder' ? [args.holder] : args.holders;
  const contextBySeatId = seatContextBySeatId(holders);
  const rows = buildReport(
    results.map((result) => ({ id: result.rosterSeatId, outcome: result.outcome })),
    contextBySeatId,
    landedWord,
    holdersWithoutEligibleSeat(holders, eligible),
  );

  const { landed, total } = studentTally(rows);
  const isRemoval = landedWord === BATCH_LANDED.remove;
  const palette = seatActions.cohortPalette;
  const base = {
    landed,
    total,
    landedHolderIds: landedHolderIds(results, contextBySeatId, landedWord),
  };

  // A batch nobody was in reports nothing, and "0 of 0 landed" would render as a clean
  // success. The doors reject a short answer before it reaches here; this says so anyway
  // rather than borrowing that guard.
  if (total === 0) {
    return { ...base, message: strings.nothingReported, severity: 'error' };
  }

  if (landed === total) {
    return {
      ...base,
      message:
        args.voice === 'holder'
          ? (isRemoval ? palette.removed : palette.added)(args.holder.name, cohortName)
          : (isRemoval ? strings.removed : strings.added)(landed, cohortName),
      severity: 'success',
    };
  }

  if (landed === 0) {
    const first = rows.find((row) => !row.landed);

    return {
      ...base,
      message:
        args.voice === 'holder' && first !== undefined
          ? strings.noneChanged(first.name, outcomeProse(first.outcome))
          : strings.noneChangedMany(total, refusedNames(rows)),
      severity: 'error',
    };
  }

  return {
    ...base,
    message: (isRemoval ? strings.partiallyRemoved : strings.partiallyAdded)(
      landed,
      total,
      cohortName,
      refusedNames(rows),
    ),
    severity: 'warning',
  };
};

/**
 * The message for an error that came back past a commit, or null where it did not. The
 * seats have already changed by then and only the report was lost, so reporting it as a
 * refusal would invite a retry that refuses for a different reason.
 */
export const landedUnreadableMessage = (error: unknown): string | null =>
  isLandedUnreadable(error) ? seatActions.landedUnreadable.batch : null;
